import { useQuery } from "@apollo/client";
import { BRAND_NAME, MembershipStatus } from "@app/shared/constants";
import { Membership, ProductFeature } from "@app/shared/types";
import { GRAPHQL_QUERY_MEMBERSHIPS } from "app/queries";
import DocumentContext from "components/DocumentContext";
import { GenericErrorPage } from "components/GenericErrorPage";
import { LoggedInUserProfileContext } from "features/auth/RequireAuthentication";
import { ContributePage } from "features/donation/ContributePage";
import { DonatePage } from "features/donation/DonatePage";
import { DonationSuccessPage } from "features/donation/DonationSuccessPage";
import { SetIsMember } from "hooks/useMemberTracking";
import _ from "lodash";
import { useContext } from "react";
import { useSelector } from "react-redux";
import { Redirect, Route, Switch, useLocation } from "react-router";
import { routes } from "../../app/routes";
import { selectIsMember, selectUser } from "../auth/auth";
import LoadingPage from "../pages/LoadingPage";
import { MemberAccountPage } from "./account/MemberAccountPage";
import { ReviewMentorshipAddOn } from "./addOnMentorshipGroup/ReviewMentorshipAddOn";
import { MemberHomePage } from "./dashboard/MemberHomePage";
import { MemberSchedulePage } from "./dashboard/MemberSchedulePage";
import { DropInSessionsMeditationsDiscussionsPage } from "./dropInSessions/DropInSessionsMeditationsDiscussionsPage";
import { DropInSessionsTalksPage } from "./dropInSessions/DropInSessionsTalksPage";

import ConflictResolutionPage from "features/pages/ConflictResolutionPage";
import { SetupShowYouAroundPage } from "features/setup/SetupShowYouAroundPage";
import { SetupWelcomeSessionPage } from "features/setup/SetupWelcomeSessionPage";
import { MemberSessionsRightNowPage } from "./dashboard/MemberSessionsRightNowPage";
import { DropInSessionsAllSessionsPage } from "./dropInSessions/DropInSessionsAllSessionsPage";
import { DropInSessionsMonthlyThemePage } from "./dropInSessions/DropInSessionsMonthlyThemePage";
import { DropInSessionRoute } from "./dropInSessions/dropInSessionsRoute";
import { DropInSessionsSilentMeditationsPage } from "./dropInSessions/DropInSessionsSilentMeditationsPage";
import { DropInSessionsWelcomeSessionsPage } from "./dropInSessions/DropInSessionsWelcomeSessionsPage";
import { InterestGroupConfirmationPage } from "./interestGroups/InterestGroupConfirmationPage";
import { InterestGroupPage } from "./interestGroups/InterestGroupPage";
import { MemberInterestGroupsPage } from "./interestGroups/MemberInterestGroupsPage";
import { MemberContext } from "./MemberContext";
import { CancellationModal } from "./membership/CancellationModal";
import { MembershipCanceled } from "./membership/MembershipCanceled";
import { MembershipDeclined } from "./membership/MembershipDeclined";
import { MembershipNotFound } from "./membership/MembershipNotFound";
import { MembershipUpdateBilling } from "./membership/MembershipUpdateBilling";
import { UpdateLegacyBillingPage1 } from "./membership/UpdateLegacyBillingPage1";
import { UpdateLegacyBillingPage2 } from "./membership/UpdateLegacyBillingPage2";
import { UpdateLegacyBillingPage3 } from "./membership/UpdateLegacyBillingPage3";
import MySanghas from "./mySanghas/MySanghas";
import { MemberZoneEditProfilePage } from "./profile/MemberZoneEditProfilePage";
import { SetupEditProfilePage } from "./profile/SetupEditProfilePage";
import { InterestGroupConfirmationDialogTest } from "./sangha/InterestGroupConfirmationDialogTest";
import { MemberSangha } from "./sangha/MemberSangha";
import { TeachersPage } from "./teachers/TeachersPage";

export { MemberContext };

export const EnsureActiveMembership = (props: { children: JSX.Element }) => {
    const user = useContext(LoggedInUserProfileContext);
    const userProfile = user.profile;

    const isImmersion = user.features.includes(ProductFeature.DEDICATED_SANGHA);

    const {
        data: membershipData,
        loading: membershipsAreLoading,
        error: membershipsError,
    } = useQuery(GRAPHQL_QUERY_MEMBERSHIPS, {
        fetchPolicy: "network-only",
    });

    const memberships = (membershipData?.myMemberships || []) as Membership[];
    const activeMemberships = memberships.filter((m) => MembershipStatus.isActive(m.status));
    const canceledMemberships = memberships.filter((m) => m.status === MembershipStatus.Canceled);
    const pendingMemberships = memberships.filter((m) => m.status === MembershipStatus.Pending);
    const declinedMemberships = memberships.filter((m) => m.status === MembershipStatus.Declined);

    if (membershipsError) {
        return <GenericErrorPage alternateInstruction="Please reload the page or" />;
    }

    if (membershipsAreLoading) {
        return <LoadingPage />;
    }
    if (activeMemberships.length > 0) {
        if (userProfile.isComplete) {
            const context = {
                memberships: activeMemberships,
            };
            return (
                <MemberContext.Provider value={context}>{props.children}</MemberContext.Provider>
            );
        } else {
            return <SetupEditProfilePage />;
        }
    } else if (pendingMemberships.length > 0) {
        const pendingMembership = _.last(pendingMemberships) as Membership;

        if (!isImmersion) {
            // We should only have immersion members in the pending state
            return <GenericErrorPage />;
        }

        return <SetupEditProfilePage />;
    } else if (canceledMemberships.length > 0) {
        return <MembershipCanceled />;
    } else if (declinedMemberships.length > 0) {
        const declinedMembership = declinedMemberships[0];
        return <MembershipDeclined email={declinedMembership.email} />;
    } else if (!user.features.length) {
        // bugfix: if member doesn't finish billing, take them to the MFG page
        return <Redirect to={routes.signup()} />;
    } else {
        return <MembershipNotFound />;
    }
};

const MemberZoneInner = () => {
    const location = useLocation();

    return (
        <>
            <DocumentContext title={BRAND_NAME} />
            <Switch>
                <Route
                    path={routes.addMentorshipGroup(":sanghaId")}
                    component={ReviewMentorshipAddOn}
                />
                <Route path={routes.memberSanghaDetails(":sanghaId")} component={MemberSangha} />
                <Route path={routes.memberSanghas()} component={MySanghas} />
                <Route path={routes.memberSanghasDeprecated()} component={MySanghas} />
                <Route path={routes.memberUpdateBilling()} component={MembershipUpdateBilling} />
                <Route
                    path={routes.memberMembership(":stripeResult")}
                    component={MemberAccountPage}
                />
                <Route path={routes.memberMembership()} component={MemberAccountPage} />
                <Route path={routes.cancelMembership()} component={CancellationModal} />
                <Route path={routes.memberSchedulePage()} component={MemberSchedulePage} />
                <Route
                    path={routes.memberSessionsRightNow()}
                    component={MemberSessionsRightNowPage}
                />
                <Route
                    exact
                    path={routes.memberDropInSessionsMonthlyTheme()}
                    component={DropInSessionsMonthlyThemePage}
                />
                <Route
                    exact
                    path={routes.memberDropInSessionsTalks()}
                    component={DropInSessionsTalksPage}
                />
                <Route
                    exact
                    path={routes.memberDropInSessionsMeditationsDiscussions()}
                    component={DropInSessionsMeditationsDiscussionsPage}
                />
                <Route
                    exact
                    path={routes.memberDropInSessionsWelcomeSessions()}
                    component={DropInSessionsWelcomeSessionsPage}
                />
                <Route
                    exact
                    path={routes.editProfilePage()}
                    component={MemberZoneEditProfilePage}
                />
                <Route
                    exact
                    path={routes.setupEditProfilePage()}
                    component={SetupEditProfilePage}
                />
                <Route
                    exact
                    path={routes.setupWelcomeSession()}
                    component={SetupWelcomeSessionPage}
                />
                <Route
                    exact
                    path={routes.setupShowYouAround()}
                    component={SetupShowYouAroundPage}
                />
                <Route
                    exact
                    path={routes.memberDropInSessionsAllSessions()}
                    component={DropInSessionsAllSessionsPage}
                />
                <Route
                    exact
                    path={routes.memberDropInSessionsSilentMeditations()}
                    component={DropInSessionsSilentMeditationsPage}
                />

                <Route path={routes.memberMindfulFriends()} component={MemberInterestGroupsPage} />
                <Route path={routes.memberTeachersPage()} component={TeachersPage} />
                <Route path={routes.conflictResolution()} component={ConflictResolutionPage} />
                <Route path={routes.memberContribute()} component={ContributePage} />
                <Route path={routes.memberDonationSuccess()} component={DonationSuccessPage} />
                <Route path={routes.memberDonate()} component={DonatePage} />
                <Route
                    path={routes.memberUpdateLegacyBillingPage1()}
                    component={UpdateLegacyBillingPage1}
                />
                <Route
                    path={routes.memberUpdateLegacyBillingPage2()}
                    component={UpdateLegacyBillingPage2}
                />
                <Route
                    path={routes.memberUpdateLegacyBillingPage3()}
                    component={UpdateLegacyBillingPage3}
                />
                <Route
                    path={routes.memberConfirmInterestGroup(":groupId")}
                    component={InterestGroupConfirmationPage}
                />
                <Route
                    path={routes.memberAddInterestGroup(":groupId")}
                    component={InterestGroupPage}
                />

                <Route
                    path={routes.testInterestGroupGuidelines()}
                    component={InterestGroupConfirmationDialogTest}
                />
                <Route path={routes.memberHomePage()} component={MemberHomePage} />
                <Route path={routes.memberDropInSessions()} component={DropInSessionRoute} />

                <Redirect to={routes.memberHomePage()} />
            </Switch>
        </>
    );
};

export const MemberZone = (props: { location: { search: any; state: any } }) => {
    const isMember = useSelector(selectIsMember);
    const user = useSelector(selectUser);

    if (!user) {
        return <LoadingPage />;
    }

    if (isMember) {
        /* Sets a cookie to track whether the user is a member or not. */
        SetIsMember(isMember);
        return (
            <EnsureActiveMembership>
                <MemberZoneInner />
            </EnsureActiveMembership>
        );
    } else {
        // teacher or admin
        return <MemberZoneInner />;
    }
};

export default MemberZone;
